import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Plus, TrendingUp, Activity as ActivityIcon, ListChecks } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { StatCard, ProgressCard, Spinner, EmptyState, StatusBadge } from "@/components/common/Common";
import api from "@/lib/api";
import { inr } from "@/lib/constants";
import { Button } from "@/components/ui/button";

export default function HomeownerDashboard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard/home").then(({ data }) => setData(data)); }, []);

  if (!data) return <AppShell variant="home" title="Home"><Spinner label="Loading your home..." /></AppShell>;

  if (!data.primary) {
    return (
      <AppShell variant="home" title="Home">
        <EmptyState testid="no-home-project" title="You don't have any home project yet."
          subtitle="Create your home project to unlock your workspace, timeline, budget and AI advisor."
          actionLabel="Create Your First Project" onAction={() => nav("/onboarding/home")} />
      </AppShell>
    );
  }

  const p = data.primary;
  const spent = data.budget?.spent || 0, planned = data.budget?.planned || 0;
  return (
    <AppShell variant="home" title="Home">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="sp-eyebrow !text-sp-orange">{p.id}</div>
          <h1 className="text-2xl font-bold text-sp-navy mt-1">{p.name}</h1>
          <p className="text-sm text-slate-500">{p.builtup_area} sq.ft · {p.location} · {p.floors} · {inr(p.budget)} budget</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="open-workspace" onClick={() => nav(`/app/project/${p.id}`)} className="bg-sp-navy hover:bg-sp-charcoal rounded-full px-5">Open Workspace <ArrowRight className="ml-1 h-4 w-4" /></Button>
          <Button data-testid="new-home-project" variant="outline" onClick={() => nav("/onboarding/home")} className="rounded-full"><Plus className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ProgressCard label="Project Progress" value={data.progress} sub={`${p.status}`} />
        <StatCard testid="stat-budget" label="Budget" value={`${inr(spent)} / ${inr(planned)}`} sub={`${inr(planned - spent)} remaining`} accent />
        <StatCard label="Timeline" value={`${p.timeline || "12 months"}`} sub="Estimated duration" />
        <ProgressCard label="Home Health" value={data.health} sub="Intelligent score" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Project Timeline" icon={TrendingUp} action={<Button variant="ghost" size="sm" onClick={() => nav(`/app/project/${p.id}`)} className="text-sp-orange">View all</Button>}>
            <div className="flex gap-2 overflow-x-auto sp-scroll pb-2">
              {data.stages.map((s) => (
                <div key={s.id} className="shrink-0 w-28 rounded-lg border border-slate-200 p-3">
                  <div className="text-xs font-medium text-sp-navy truncate">{s.name}</div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-sp-orange" style={{ width: `${s.progress}%` }} /></div>
                  <div className="text-[11px] text-slate-400 mt-1.5">{s.progress}%</div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Upcoming Tasks" icon={ListChecks}>
            {data.upcoming_tasks.length === 0 ? <p className="text-sm text-slate-400 py-4">No pending tasks.</p> :
              <div className="space-y-2">{data.upcoming_tasks.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div><div className="text-sm text-sp-navy">{t.title}</div><div className="text-xs text-slate-400">{t.stage}</div></div>
                  <StatusBadge status={t.status} />
                </div>
              ))}</div>}
          </Card>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl bg-sp-navy text-white p-5 sp-blueprint">
            <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-sp-orange" /><span className="font-semibold text-sm">AI Recommendations</span></div>
            <ul className="space-y-2.5">{data.recommendations.map((r, i) => <li key={i} className="text-sm text-white/75 leading-snug flex gap-2"><span className="text-sp-orange">→</span>{r}</li>)}</ul>
            <Button data-testid="open-advisor" onClick={() => nav("/app/copilot")} className="mt-4 w-full bg-sp-orange hover:bg-sp-orangedark rounded-full">Ask AI Advisor</Button>
          </div>
          <Card title="Recent Activity" icon={ActivityIcon}>
            {data.recent_activity.length === 0 ? <p className="text-sm text-slate-400 py-2">No activity yet.</p> :
              <div className="space-y-3">{data.recent_activity.map((a) => (
                <div key={a.id} className="text-sm"><span className="text-slate-400">{a.user_name}</span> <span className="text-sp-navy">{a.action}</span></div>
              ))}</div>}
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function Card({ title, icon: Icon, children, action }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-sp-orange" /><span className="font-semibold text-sp-navy">{title}</span></div>
        {action}
      </div>
      {children}
    </div>
  );
}
